import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from "react";
import type { BootstrapPayload } from "../../shared/contracts/bootstrap.js";

type AvatarIdentity = {
  accountId?: string | null | undefined;
  participantId?: string | null | undefined;
  self?: boolean | undefined;
};
const AvatarContext = createContext<
  (identity: AvatarIdentity) => string | null
>(() => null);
/** Identity-based images; never match people by their names. */
export function AvatarProvider(props: {
  bootstrap: BootstrapPayload;
  enabled: boolean;
  children: ReactNode;
}): React.JSX.Element {
  const [revision, setRevision] = useState(() => Date.now());
  useEffect(() => {
    const refresh = () => setRevision(Date.now());
    const visible = () => {
      if (document.visibilityState === "visible") refresh();
    };
    window.addEventListener("fairway:avatar-changed", refresh);
    window.addEventListener("focus", refresh);
    document.addEventListener("visibilitychange", visible);
    const channel =
      typeof BroadcastChannel === "undefined"
        ? null
        : new BroadcastChannel("fairway-avatar");
    if (channel) channel.onmessage = refresh;
    return () => {
      window.removeEventListener("fairway:avatar-changed", refresh);
      window.removeEventListener("focus", refresh);
      document.removeEventListener("visibilitychange", visible);
      channel?.close();
    };
  }, []);
  const source = (identity: AvatarIdentity): string | null => {
    if (!props.enabled) return null;
    const person = props.bootstrap.participants.find((p) =>
      identity.participantId
        ? p.id === identity.participantId
        : !!identity.accountId && p.accountId === identity.accountId,
    );
    const own =
      identity.self ||
      identity.accountId === props.bootstrap.account.id ||
      person?.accountId === props.bootstrap.account.id;
    if (own) return `/api/v1/profile/avatar?v=${revision}`;
    const id = identity.participantId ?? person?.id;
    return id
      ? `/api/v1/participants/${encodeURIComponent(id)}/avatar?v=${revision}`
      : null;
  };
  return (
    <AvatarContext.Provider value={source}>
      {props.children}
    </AvatarContext.Provider>
  );
}
export function useAvatarSource(identity: AvatarIdentity): string | null {
  return useContext(AvatarContext)(identity);
}
export function useAvatarResolver() {
  return useContext(AvatarContext);
}
export function announceAvatarChanged(): void {
  window.dispatchEvent(new Event("fairway:avatar-changed"));
  if (typeof BroadcastChannel !== "undefined") {
    const channel = new BroadcastChannel("fairway-avatar");
    channel.postMessage("changed");
    channel.close();
  }
}
